<template>
  <div class="order-suggestions">
    <div class="page-head">
      <div>
        <h3>Order Suggestions</h3>
        <p class="page-head-sub">
          What to restock, ranked by demand<span v-if="builtAt"> · data as of {{ formatDate(builtAt) }}</span>
        </p>
      </div>
    </div>
    <b-card class="mb-3">

      <b-form @submit.prevent="preview">
        <b-form-row>
          <b-col cols="12" md="2">
            <b-form-group label="Supplier" label-for="os-supplier">
              <b-form-select id="os-supplier" v-model="filters.supplier" :options="supplierOptions" required />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="2">
            <b-form-group label="Sales period" label-for="os-months">
              <b-form-select id="os-months" v-model="filters.months" :options="monthOptions" />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="2">
            <b-form-group label="Min demand" label-for="os-min-demand">
              <b-form-input id="os-min-demand" v-model.number="filters.minDemand" type="number" min="0" placeholder="0" />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="2">
            <b-form-group label="Max supplier price PLN" label-for="os-max-price">
              <b-form-input id="os-max-price" v-model.number="filters.maxPrice" type="number" min="0" step="0.01" placeholder="no limit" />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="2">
            <b-form-group label="Min license plates" label-for="os-min-plates">
              <b-form-input id="os-min-plates" v-model.number="filters.minPlates" type="number" min="0" placeholder="0" />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="2" class="d-flex align-items-center">
            <b-form-checkbox id="os-in-stock" v-model="filters.inStock" switch class="os-stock-switch">Supplier has stock</b-form-checkbox>
          </b-col>
        </b-form-row>

        <b-form-row>
          <b-col cols="6" md="2">
            <b-form-group label="Thansen" label-for="os-thansen">
              <b-form-select id="os-thansen" v-model="filters.thansen" :options="yesNoOptions" />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="2">
            <b-form-group label="Meca" label-for="os-meca">
              <b-form-select id="os-meca" v-model="filters.meca" :options="yesNoOptions" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3">
            <b-form-group label="Cheaper alternative" label-for="os-alternative">
              <b-form-select id="os-alternative" v-model="filters.alternative" :options="alternativeOptions" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="5">
            <b-form-group label="Product groups" label-for="os-product-types">
              <v-select
                ref="productTypes"
                v-model="filters.productTypes"
                input-id="os-product-types"
                :options="unselectedProductTypes"
                :disabled="!filters.supplier"
                :close-on-select="false"
                multiple
                placeholder="All product groups"
                @option:selecting="keepListPosition"
                @option:deselecting="keepListPosition" />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row>
          <b-col cols="12" class="d-flex justify-content-md-end mb-3">
            <b-button type="submit" variant="primary" class="mr-2" :disabled="!filters.supplier || loading || downloading">
              <b-spinner v-if="loading" small class="mr-1" />
              Show
            </b-button>
            <b-button variant="success" :disabled="!filters.supplier || loading || downloading" @click="download">
              <b-spinner v-if="downloading" small class="mr-1" />
              <b-icon v-else icon="download" class="mr-1" />
              Download XLSX
            </b-button>
          </b-col>
        </b-form-row>
      </b-form>
    </b-card>

    <div v-if="result" class="mb-2 d-flex flex-wrap align-items-center">
      <b-badge variant="primary" class="mr-2 p-2">{{ result.total.toLocaleString('no') }} products</b-badge>
      <b-badge variant="warning" class="mr-2 p-2">
        {{ result.withCheaperAlternative.toLocaleString('no') }} with a cheaper alternative
      </b-badge>
      <small v-if="result.total > result.rows.length" class="text-muted">
        Showing the top {{ result.rows.length }} by demand. Download the XLSX for all rows and columns.
      </small>
    </div>

    <b-table
      v-if="result"
      :items="result.rows"
      :fields="fields"
      small
      striped
      hover
      responsive
      sticky-header="65vh"
      head-variant="dark"
      show-empty
      empty-text="No products match these filters"
      class="order-suggestions-table">
      <template #cell(product)="{ item }">
        <div class="os-product" @click="showProduct(item.codename)">
          <div class="font-weight-bold">
            {{ item.brand }} {{ item.artNr }}
            <b-spinner v-if="opening === item.codename" small class="ml-1" />
          </div>
          <small class="text-muted">{{ item.productType }} · {{ item.codename }}</small>
        </div>
      </template>
      <template #head(demand)>Demand {{ result.months }}M</template>
      <template #cell(demand)="{ item }">
        <span class="font-weight-bold">{{ item.totalDemand }}</span>
        <small class="text-muted d-block">{{ item.exactSales }} + {{ item.equivalentSales }} OE</small>
      </template>
      <template #cell(competitors)="{ item }">
        <b-badge :variant="item.thansenOE ? 'info' : 'secondary'" class="mr-1">T</b-badge>
        <b-badge :variant="item.mecaOE ? 'info' : 'secondary'">M</b-badge>
      </template>
      <template #cell(replacement)="{ item }">
        <template v-if="item.replacementPrice">
          <div>{{ item.cheapestReplacement }}</div>
          <small class="text-muted">{{ item.replacementBrand }} {{ item.replacementNumber }}</small>
        </template>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(savings)="{ item }">
        <template v-if="item.savingsPLN">
          <span class="text-success font-weight-bold">{{ price(item.savingsPLN) }}</span>
          <small class="text-muted d-block">{{ item.savingsPercent }}%</small>
        </template>
      </template>
      <template #cell(localStock)="{ item }">
        {{ item.sourceLocalStock }}
        <small class="text-muted d-block">
          repl. {{ item.replacementLocalStock == null ? '—' : item.replacementLocalStock }} · OE {{ item.anyOELocalStock }}
        </small>
      </template>
    </b-table>

    <product-details ref="details" />
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import ProductDetails from '@/components/ProductDetails.vue';
import fixed from '../../fixed';

const price = (v) => (v == null ? '—' : v.toFixed(2));

export default {
  name: 'OrderSuggestions',
  components: { ProductDetails, vSelect },
  data: () => ({
    suppliers: [],
    productTypes: [],
    filters: {
      supplier: null,
      maxPrice: null,
      minDemand: null,
      minPlates: null,
      inStock: true,
      months: 12,
      thansen: '',
      meca: '',
      alternative: '',
      productTypes: [],
    },
    yesNoOptions: [
      { value: '', text: 'Any' },
      { value: 'yes', text: 'Has it' },
      { value: 'no', text: 'Doesn\'t have it' },
    ],
    monthOptions: [3, 6, 9, 12].map((m) => ({ value: m, text: `Last ${m} months` })),
    alternativeOptions: [
      { value: '', text: 'Any' },
      { value: 'none', text: 'None: this supplier is cheapest' },
      { value: 'exists', text: 'Exists: cheaper elsewhere' },
    ],
    fields: [
      { key: 'product', label: 'Product' },
      {
        key: 'supplierPricePLN', label: 'Price PLN', formatter: price, class: 'text-right',
      },
      { key: 'supplierStock', label: 'Supplier stock', class: 'text-right' },
      { key: 'demand', label: 'Demand', class: 'text-right' },
      { key: 'norwayKTypes', label: 'Vehicles', class: 'text-right' },
      { key: 'norwayLicensePlates', label: 'Plates', class: 'text-right' },
      { key: 'competitors', label: 'Thansen / Meca', class: 'text-center' },
      { key: 'replacement', label: 'Cheapest replacement' },
      {
        key: 'replacementPrice', label: 'Repl. PLN', formatter: price, class: 'text-right',
      },
      { key: 'savings', label: 'Saving', class: 'text-right' },
      { key: 'localStock', label: 'Our stock', class: 'text-right' },
    ],
    result: null,
    builtAt: null,
    loading: false,
    downloading: false,
    opening: null, // codename whose details are loading
  }),
  computed: {
    supplierOptions() {
      return [{ value: null, text: 'Choose supplier', disabled: true }, ...this.suppliers];
    },
    unselectedProductTypes() {
      const selected = new Set(this.filters.productTypes);
      return this.productTypes.filter((type) => !selected.has(type));
    },
  },
  watch: {
    'filters.supplier': 'loadProductTypes',
  },
  async mounted() {
    try {
      const { data } = await axios.get(`${fixed.API_URL}/admin/order-suggestions/suppliers`, { withCredentials: true });
      this.suppliers = data || [];
      if (!this.suppliers.length) this.$toast.info('No order insights yet. The nightly build hasn\'t run.');
    } catch (error) {
      this.showError(error, 'can\'t load suppliers');
    }
  },
  methods: {
    price,
    formatDate(value) {
      return moment(value).format('DD.MM.yyyy HH:mm');
    },
    // Each supplier has its own groups, so the previous selection is dropped.
    // A slower response for an earlier supplier is ignored.
    async loadProductTypes(supplier) {
      this.filters.productTypes = [];
      this.productTypes = [];
      try {
        const { data } = await axios.get(`${fixed.API_URL}/admin/order-suggestions/product-types`, {
          params: { supplier },
          withCredentials: true,
        });
        if (supplier === this.filters.supplier) this.productTypes = data;
      } catch (error) {
        this.showError(error, 'can\'t load product groups');
      }
    },
    // Selecting changes the options, so vue-select moves its pointer to the first
    // row and scrolls there. Its `autoscroll` prop can't help: arrow keys need it.
    // Restored in a frame, after Vue's flush and before the browser paints.
    keepListPosition() {
      const select = this.$refs.productTypes;
      const menu = select.$refs.dropdownMenu;
      if (!menu) return;
      const { scrollTop } = menu;
      const pointer = select.typeAheadPointer;
      requestAnimationFrame(() => {
        menu.scrollTop = scrollTop;
        select.typeAheadPointer = Math.min(pointer, select.filteredOptions.length - 1);
      });
    },
    // params drops empty filters so the API applies its defaults. Arrays repeat
    // their key, since product group names can contain commas.
    params(format) {
      const params = new URLSearchParams({ format });
      Object.entries(this.filters).forEach(([key, filter]) => {
        [].concat(filter).forEach((value) => {
          if (value !== null && value !== '' && value !== false) params.append(key, value);
        });
      });
      return params;
    },
    async preview() {
      this.loading = true;
      try {
        const { data } = await axios.get(`${fixed.API_URL}/admin/order-suggestions`, {
          params: this.params('json'),
          withCredentials: true,
        });
        this.result = data;
        this.builtAt = data.builtAt;
      } catch (error) {
        this.showError(error, 'can\'t load order suggestions');
      }
      this.loading = false;
    },
    async download() {
      this.downloading = true;
      try {
        const { data } = await axios.get(`${fixed.API_URL}/admin/order-suggestions`, {
          params: this.params('xlsx'),
          withCredentials: true,
          responseType: 'blob',
        });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(data);
        link.download = `order-suggestions-${this.filters.supplier}-${moment().format('YYYY-MM-DD')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } catch (error) {
        this.showError(error, 'can\'t download order suggestions');
      }
      this.downloading = false;
    },
    // Same popup as /local-stock's product name; the rows only carry the
    // codename, so the full product is fetched first.
    async showProduct(codename) {
      if (this.opening) return;
      this.opening = codename;
      try {
        const { data } = await axios.get(`${fixed.API_URL}/admin/sku2article`, {
          params: { sku: codename },
          withCredentials: true,
        });
        if (!this.$refs.details.show(data)) this.$toast.info(`No image or OEM codes for ${codename}`);
      } catch (error) {
        this.showError(error, `can't load ${codename}`);
      }
      this.opening = null;
    },
    async showError(error, fallback) {
      const status = error?.response?.status;
      if (status === 403) {
        this.$toast.warning('you\'re not authorised for this action ✋🏻');
        return;
      }
      // Errors of blob requests arrive as a Blob too.
      let body = error?.response?.data;
      if (body instanceof Blob) {
        try {
          body = JSON.parse(await body.text());
        } catch (e) {
          body = null;
        }
      }
      this.$toast.error(body?.error || fallback);
    },
  },
};
</script>

<style scoped>
.order-suggestions-table {
  font-size: 0.85rem;
}

.os-product {
  cursor: pointer;
}
.os-product:hover .font-weight-bold {
  text-decoration: underline;
}

/* A bigger switch with smaller text than BootstrapVue's size="lg" allows:
   3rem x 1.75rem track, knob inset 3px, label vertically centred on it. */
.os-stock-switch {
  padding-left: 3.75rem;
}
.os-stock-switch ::v-deep .custom-control-label {
  font-size: 0.9rem;
  line-height: 1.75rem;
}
.os-stock-switch ::v-deep .custom-control-label::before {
  top: 0;
  left: -3.75rem;
  width: 3rem;
  height: 1.75rem;
  border-radius: 1rem;
}
.os-stock-switch ::v-deep .custom-control-label::after {
  top: 3px;
  left: calc(-3.75rem + 3px);
  width: calc(1.75rem - 6px);
  height: calc(1.75rem - 6px);
  border-radius: 50%;
}
.os-stock-switch ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  transform: translateX(1.25rem);
}
</style>
